import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import {
  startCreatePublishingAutomation,
  startListenerPublishingAutomations,
  startPausePublishingAutomation,
  startRenamePublishingAutomation,
  startResumePublishingAutomation,
  startTurnOffPublishingAutomation,
  startUpdatePublishingAutomation,
  stopPublishingAutomationListeners,
} from "../actions/publishingAutomations";
import { mapAutomationToCard } from "../utils/publishingAutomationMappers";

export const usePublishingAutomations = () => {
  const dispatch = useDispatchApp();
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID || state.auth?.user?.shopID || ""
  );
  const [automationsMap, setAutomationsMap] = useState({});
  const [runtimeMap, setRuntimeMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [statusOverrides, setStatusOverrides] = useState({});
  const referencesRef = useRef({});

  useEffect(() => {
    let isMounted = true;
    setAutomationsMap({});
    setRuntimeMap({});
    setIsLoading(true);

    if (!advertiserID) {
      setIsLoading(false);
      return undefined;
    }

    dispatch(
      startListenerPublishingAutomations({
        onAutomationsChange: (value) => {
          if (!isMounted) return;
          setAutomationsMap(value || {});
          setIsLoading(false);
        },
        onRuntimeChange: (value) => {
          if (!isMounted) return;
          setRuntimeMap(value || {});
        },
        onReferencesAvailable: (references) => {
          referencesRef.current = references;
        },
      })
    );

    return () => {
      isMounted = false;
      stopPublishingAutomationListeners(referencesRef.current);
      referencesRef.current = {};
    };
  }, [advertiserID, dispatch]);

  const automations = useMemo(
    () =>
      Object.entries(automationsMap)
        .map(([automationID, automation]) => {
          const item = mapAutomationToCard({
            automationID,
            automation,
            runtime: runtimeMap[automationID] || {},
          });
          if (statusOverrides[automationID] === undefined) return item;
          return {
            ...item,
            active: statusOverrides[automationID],
          };
        })
        .sort(
          (left, right) => (right.raw?.createdAt || 0) - (left.raw?.createdAt || 0)
        ),
    [automationsMap, runtimeMap, statusOverrides]
  );

  useEffect(() => {
    const matchedIDs = Object.keys(statusOverrides).filter((automationID) => {
      const item = automationsMap[automationID];
      if (!item) return false;
      const mapped = mapAutomationToCard({
        automationID,
        automation: item,
        runtime: runtimeMap[automationID] || {},
      });
      return mapped.active === statusOverrides[automationID];
    });
    if (matchedIDs.length === 0) return;
    setStatusOverrides((prev) => {
      const next = { ...prev };
      matchedIDs.forEach((automationID) => {
        delete next[automationID];
      });
      return next;
    });
  }, [automationsMap, runtimeMap, statusOverrides]);

  const createAutomation = useCallback(
    (payload) => dispatch(startCreatePublishingAutomation(payload)),
    [dispatch]
  );
  const updateAutomation = useCallback(
    (payload) => dispatch(startUpdatePublishingAutomation(payload)),
    [dispatch]
  );
  const pauseAutomation = useCallback(
    (automationID, optimisticSwitch) =>
      dispatch(
        startPausePublishingAutomation({
          automationID,
          optimisticSwitch,
        })
      ),
    [dispatch]
  );
  const resumeAutomation = useCallback(
    (automationID, optimisticSwitch) =>
      dispatch(
        startResumePublishingAutomation({
          automationID,
          optimisticSwitch,
        })
      ),
    [dispatch]
  );
  const turnOffAutomation = useCallback(
    (automationID) =>
      dispatch(startTurnOffPublishingAutomation({ automationID })),
    [dispatch]
  );
  const renameAutomation = useCallback(
    (automationID, name) =>
      dispatch(startRenamePublishingAutomation({ automationID, name })),
    [dispatch]
  );

  return {
    automations,
    isLoading,
    statusPendingID: "",
    setStatusOverride: setStatusOverrides,
    createAutomation,
    updateAutomation,
    pauseAutomation,
    resumeAutomation,
    turnOffAutomation,
    renameAutomation,
  };
};
